import { addMonths, addYears, isEqual, startOfDay, startOfMonth, } from 'date-fns';
import { TriStateType } from '../types';
/**
 * Use this function to throw an error in dev mode, but warn in production.
 * Used to enforce stricter rules without breaking backwards compatibility.
 *
 * @param messages the error messages (console.log style arguments)
 */
export function devError(...messages) {
    if (process.env.NODE_ENV === 'development') {
        throw new Error(messages.join(' '));
    }
    console.error(...messages);
}
/**
 * Can be used in Array.filter() to obtain unique values
 *
 * Example:
 * ['a', 'b', 'b'].filter(unique)
 * =>
 * ['a', 'b']
 */
export function unique(value, index, self) {
    return self.indexOf(value) === index;
}
export function getFirstValueFromArray(elements) {
    return elements && elements?.length > 0 ? elements[0] : null;
}
export function objectToQueryParams(obj) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(obj)) {
        if (value === undefined)
            continue;
        params.append(key, String(value));
    }
    return params;
}
/** Always returns an array. If the passed argument already is an array, it is returned as is.
 *  Otherwise a new array with the passed argument as the first and only element is returned.
 *  If the passed value is undefined or null, it will return an array with undefined or null as
 *  its first element. It's best to handle these cases outside of this function.
 *
 *  The following example will return an empty array if input is undefined or null.
 *
 *  const input = undefined;
 *  const output = toArray(input ?? []); // output = []
 */
export function toArray(value) {
    return Array.isArray(value)
        ? value
        : [value];
}
/** Always returns a primitive (string, number, boolean). If the passed argument already is a primitive,
 *  it is returned as is. Otherwise the first element of the array is returned. If the array is empty,
 *  the defaultValue argument is returned.
 *
 *  const input: string[] | undefined = ['asdf'];
 *  toPrimitive(input, '');
 *
 *  toPrimitive(['asdf'], '');  // string
 *  toPrimitive(['asdf'], '');  // string
 *  toPrimitive(null, '');      // null
 *  toPrimitive([], undefined); // undefined
 */
export function toPrimitive(value, defaultValue) {
    const primitive = Array.isArray(value)
        ? value.length === 0
            ? defaultValue
            : value.pop() // help typescript to remove undefined from pop()
        : value;
    // this is fairly simple runtime code, but heavy on the types - maybe there is a better way, but all in all this should be safe
    return primitive;
}
export const handleValueUnits = (value, unit) => {
    if (!value) {
        return '-';
    }
    return value + unit;
};
export function createUnits(value, type, context, t) {
    const strValue = String(value);
    let strOrFalsyValue = String(value);
    if (!value) {
        strOrFalsyValue = undefined;
    }
    switch (type) {
        case 'ccm':
            return handleValueUnits(strOrFalsyValue, ' ccm');
        case 'co2':
            return handleValueUnits(strOrFalsyValue, ' g/km');
        case 'kg':
            return handleValueUnits(strOrFalsyValue, ' kg');
        case 'PS':
            return handleValueUnits(strOrFalsyValue, ' PS');
        case 'kW':
            return handleValueUnits(strOrFalsyValue, ' kW');
        case 'date':
            return context?.formatDateTime(strValue);
        case 'currency':
            return context?.formatMoneyAmount(Math.max(Number.parseInt(strValue), 0));
        case 'noUnit':
            return strValue;
        case 'TriState':
            return TriStateType.fromString(strValue) === TriStateType.TRUE
                ? t && `${t('common:yes')}`
                : t && `${t('common:no')}`;
        default:
            return null;
    }
}
/**
 * getOneYearFirstOfMonth is used to calculate the end dates
 * for insurance contracts.
 *
 * @param startDate
 * @returns date in one year and first of month
 */
export function getOneYearFirstOfMonth(startDate) {
    const date = startOfDay(addYears(startDate ? new Date(startDate) : new Date(), 1));
    const start = startOfMonth(date);
    return isEqual(start, date) ? start : startOfMonth(addMonths(date, 1));
}
/**
 * getYearPlusXFirstOfMonth is used to calculate the end dates
 * for insurance contracts.
 *
 * @param startDate
 * @returns date in x year and first of month
 */
export function getYearPlusXFirstOfMonth(startDate, years) {
    const date = startOfDay(addYears(startDate ? new Date(startDate) : new Date(), years));
    const start = startOfMonth(date);
    return isEqual(start, date) ? start : startOfMonth(addMonths(date, 1));
}
export * from './dateOptionsUtils';
export * from './eventUtils';
export * from './insuranceSegmentUtils';
export * from './numeralOptionsUtils';
export * from './objectUtils';
export * from './typeUtils';
export * from './unitConversion';
